import React, { forwardRef } from 'react';
import type { LucideIcon } from 'lucide-react';

interface FormFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  helperText?: string;
  icon?: LucideIcon;
  required?: boolean;
}

/**
 * FormField Component
 * 
 * Accessible form input with label, error handling, and helper text.
 * Includes proper ARIA attributes for screen readers.
 * 
 * @example
 * <FormField
 *   label="Email Address"
 *   type="email"
 *   placeholder="you@company.com"
 *   error={errors.email}
 *   required
 * />
 */
export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(({
  label,
  error,
  helperText,
  icon: Icon,
  required,
  id,
  className = '',
  ...props
}, ref) => {
  const inputId = id || `field-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const errorId = `${inputId}-error`;
  const helperId = `${inputId}-helper`;

  return (
    <div className="w-full">
      <label 
        htmlFor={inputId}
        className="block text-sm font-medium text-white mb-2"
      >
        {label}
        {required && (
          <span className="text-red-400 ml-1" aria-label="required">*</span>
        )}
      </label>
      
      <div className="relative">
        {Icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Icon className="h-5 w-5 text-slate-muted" aria-hidden="true" />
          </div>
        )}
        
        <input
          ref={ref}
          id={inputId}
          className={`
            w-full bg-navy-light border rounded-lg text-white placeholder-slate-muted
            focus:outline-none focus:ring-2 focus:ring-teal focus:border-transparent
            disabled:opacity-50 disabled:cursor-not-allowed
            ${Icon ? 'pl-10' : 'pl-4'}
            pr-4
            py-2.5
            ${error ? 'border-red-500' : 'border-slate/20 hover:border-slate/40'}
            ${className}
          `}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={[
            error && errorId,
            helperText && helperId,
          ].filter(Boolean).join(' ') || undefined}
          {...props}
        />
      </div>
      
      {helperText && !error && (
        <p id={helperId} className="mt-1.5 text-sm text-slate-muted">
          {helperText}
        </p>
      )}
      
      {error && (
        <p id={errorId} className="mt-1.5 text-sm text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

FormField.displayName = 'FormField';

export default FormField;
